import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getConfig, getCriancaAtiva } from "../../db/queries";
import { elegiveisEscolhaMusical } from "../domain";
import { listarExperiencias, registrarEscolhaCrianca } from "../queries";
import { idsComAudio, tocarAudio } from "../audio";
import { PassoEngajamento, type ExperienciaMusical } from "../types";
import { ExperienciaFoto } from "../ui/ExperienciaFoto";

/**
 * P0.3 — modo escolha musical da criança. Tela cheia, mídia grande, alvos enormes.
 * Mostra 2–3 opções elegíveis; a criança toca UMA → o áudio toca na hora (feedback
 * inequívoco e celebratório), a escolhida ganha destaque e as outras desbotam (nunca
 * apagadas). Gera um momento origem=crianca (pendente). "trocar" recomeça.
 *
 * O passo registrado é "exploração" (engajamento positivo da escolha); o cuidador
 * confirma e ajusta o passo observado depois. A sessão reseta a cada entrada.
 */
const PASSO_DA_ESCOLHA = PassoEngajamento.Exploracao;

export function ModoEscolhaMusical() {
  const nav = useNavigate();
  const location = useLocation();
  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const experiencias = useLiveQuery(() => listarExperiencias()) ?? [];

  const [escolhido, setEscolhido] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const registrouRef = useRef(false);

  const qtd = Math.max(2, Math.min(3, cfg?.musica_escolha_qtd ?? 2));

  // ids com áudio tocável agora — pra preferir (não exigir) cards que dão som
  const audioIds =
    useLiveQuery(
      () => idsComAudio(elegiveisEscolhaMusical(experiencias)),
      [experiencias],
    ) ?? new Set<string>();

  // sorteia um subconjunto estável por entrada (location.key) das elegíveis
  const baralhado = useMemo(() => {
    const elegiveis = elegiveisEscolhaMusical(experiencias);
    return elegiveis
      .map((e) => ({ e, r: Math.random() }))
      .sort((a, b) => a.r - b.r)
      .map((x) => x.e);
    // location.key entra de propósito pra re-sortear a cada nova entrada
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiencias.length, location.key]);

  // tolerante: experiências com áudio vêm primeiro (feedback celebratório), mas
  // as sem clipe (ex.: instrumento físico) seguem elegíveis — honrar a escolha.
  const opcoes = useMemo(() => {
    const comAudio = baralhado.filter((e) => audioIds.has(e.id));
    const semAudio = baralhado.filter((e) => !audioIds.has(e.id));
    return [...comAudio, ...semAudio].slice(0, qtd);
  }, [baralhado, audioIds, qtd]);

  // reseta a sessão a cada entrada nova
  useEffect(() => {
    setEscolhido(null);
    registrouRef.current = false;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, [location.key]);

  async function escolher(exp: ExperienciaMusical) {
    if (escolhido || registrouRef.current || !crianca) return;
    registrouRef.current = true;
    setEscolhido(exp.id);
    audioRef.current = await tocarAudio(exp); // toca na hora (se houver áudio)
    await registrarEscolhaCrianca(exp.id, crianca.id, PASSO_DA_ESCOLHA);
  }

  function trocar() {
    setEscolhido(null);
    registrouRef.current = false;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }

  // sem experiências elegíveis
  if (opcoes.length === 0) {
    return (
      <div className="crianca">
        <div className="crianca-combinado">
          <span className="emoji" aria-hidden>
            🎵
          </span>
          <span style={{ color: "var(--text-muted)", fontSize: "1.1rem" }}>
            marque experiências como favoritas ou em exploração pra criança escolher.
          </span>
          <button className="btn btn-primario btn-grande" onClick={() => nav("/compasso/biblioteca")}>
            abrir biblioteca
          </button>
          <button className="btn btn-grande" onClick={() => nav("/compasso")}>
            voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="crianca">
      <button className="btn-voltar" onClick={() => nav("/compasso")} aria-label="sair do modo criança">
        ←
      </button>
      <div className="crianca-prompt">{escolhido ? "tocando! 🎶" : "qual música você quer?"}</div>

      <div className="crianca-opcoes grade">
        {opcoes.map((e) => {
          const sel = escolhido === e.id;
          const desbotado = escolhido !== null && !sel;
          return (
            <button
              key={e.id}
              className={`crianca-card${sel ? " escolhido" : ""}${desbotado ? " desbotado" : ""}`}
              aria-pressed={sel}
              onClick={() => escolher(e)}
            >
              <ExperienciaFoto midias={e.midias} nome={e.nome} tipo={e.tipo} />
              <span className="rotulo">
                {sel ? "✓ " : ""}
                {e.nome}
              </span>
            </button>
          );
        })}
      </div>

      <div className="crianca-barra">
        {escolhido ? (
          <>
            <button className="btn crescer btn-grande" onClick={trocar}>
              trocar
            </button>
            <button
              className="btn btn-primario crescer btn-grande"
              onClick={() => nav("/compasso")}
            >
              pronto
            </button>
          </>
        ) : (
          <button className="btn crescer btn-grande" onClick={() => nav("/compasso")}>
            sair
          </button>
        )}
      </div>
    </div>
  );
}
